<script setup>
  import BaseDialog from "@/base-components/Dialog/BaseDialog.vue";
  import Button from "@/base-components/Button";
  import InputText from "@/base-components/Form/InputText.vue";
  import { Form } from "vee-validate";
  import { ref, computed } from "vue";
  import { useI18n } from "vue-i18n";
  import Lucide from "@/base-components/Lucide";
  import useValidate from "@/composables/useValidate.js";
  import { object } from "yup";

  const { t: $t } = useI18n();
  const emit = defineEmits(["addOwner", "editOwner", "close"]);
  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
  });

  const form = ref({
    email: "",
    password: "",
    confirmPassword: "",
  });
  let id = null;
  const setFormData = (domain) => {
    if (domain.Owner) form.value.email = domain.Owner.email;
    id = domain.id;
  };
  const clearForm = () => {
    form.value = {
      email: "",
      password: "",
      confirmPassword: "",
    };
  };

  defineExpose({
    setFormData,
    clearForm,
  });
  const addTitle = computed(() => {
    return props.editMode ? $t("title.editOwner") : $t("title.addOwner");
  });

  const togglePasswordViewFlag = ref(true);
  const toggleRetypePasswordViewFlag = ref(true);
  function togglePasswordView() {
    togglePasswordViewFlag.value = !togglePasswordViewFlag.value;
  }
  function toggleRetypePasswordView() {
    toggleRetypePasswordViewFlag.value = !toggleRetypePasswordViewFlag.value;
  }
  const addOwner = () => {
    const temp = { ...form.value, id };
    emit("addOwner", temp);
  };

  const validationSchemaRegister = object(
    useValidate(["email", "password", "password_confirm"])
  );
</script>
<template>
  <section>
    <BaseDialog
      staticBackDrop
      size="lg"
      :open="show"
      :has-footer="true"
      :title="$t('title.addOwner')"
      @close="emit('close')"
    >
      <Form
        class="w-full"
        id="ownerForm"
        @submit="addOwner"
        :validation-schema="validationSchemaRegister"
      >
        <!-- fake inputs to prevent browser's autofill -->
        <input type="password" class="hidden" />
        <input type="text" name="email" class="hidden" autocomplete="off" />

        <div class="px-2 pt-4 pb-8">
          <!-- email -->
          <div
            class="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-6 intro-y"
          >
            <InputText
              name="email"
              :dir="'ltr'"
              :has-label="true"
              :label="$t('label.email')"
              v-model="form.email"
              id="input-1"
              type="text"
            />
          </div>

          <!-- password -->
          <div
            class="col-span-12 lg:col-span-6 xl:col-span-6 md:col-span-6 intro-y mt-6"
          >
            <div class="relative flex mx-auto">
              <InputText
                class="w-full pe-9"
                :dir="'ltr'"
                :has-label="true"
                :label="$t('label.password')"
                name="password"
                id="input-2"
                v-model="form.password"
                :type="togglePasswordViewFlag ? 'password' : 'text'"
              />
              <div
                class="cursor-pointer flex absolute bottom-0 end-0 items-center h-[41px] justify-center w-10 border rounded-l bg-slate-100 text-slate-500 dark:bg-darkmode-700 dark:border-darkmode-800 dark:text-slate-400"
                @click="togglePasswordView"
              >
                <Lucide
                  :icon="togglePasswordViewFlag ? 'Eye' : 'EyeOff'"
                  class="w-6 h-5 text-black"
                />
              </div>
            </div>
          </div>

          <!-- confirmPass -->
          <div
            class="col-span-12 lg:col-span-6 xl:col-span-6 md:col-span-6 intro-y mt-6"
          >
            <div class="relative flex mx-auto">
              <InputText
                class="w-full pe-9"
                :dir="'ltr'"
                :has-label="true"
                :label="$t('label.retype-password')"
                name="password_confirm"
                id="input-3"
                v-model="form.confirmPassword"
                :type="toggleRetypePasswordViewFlag ? 'password' : 'text'"
              />
              <div
                class="cursor-pointer flex absolute bottom-0 end-0 items-center h-[41px] justify-center w-10 border rounded-l bg-slate-100 text-slate-500 dark:bg-darkmode-700 dark:border-darkmode-800 dark:text-slate-400"
                @click="toggleRetypePasswordView"
              >
                <Lucide
                  :icon="toggleRetypePasswordViewFlag ? 'Eye' : 'EyeOff'"
                  class="w-6 h-5 text-black"
                />
              </div>
            </div>
          </div>
        </div>
      </Form>

      <!-- footer buttons section -->
      <template v-slot:footer>
        <free-style-shimmer
          width="100%"
          height="25px"
          class="w-full"
          v-if="loadingFlag"
        />
        <div class="flex col-md-12 items-end justify-end gap-2" v-else>
          <Button
            variant="outline-secondary"
            @click="emit('close')"
            size="sm"
            class="shadow-md justify-end items-end"
          >
            {{ $t("button.cancel") }}
          </Button>
          <Button
            variant="primary"
            size="sm"
            form="ownerForm"
            class="shadow-md justify-end items-end"
          >
            {{ addTitle }}
          </Button>
        </div>
      </template>
    </BaseDialog>
  </section>
</template>
